// 3rd party imports
import * as React from 'react';
import { useHistory, Link } from 'react-router-dom';
import { inject } from 'mobx-react';

// shared-ui
import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

import { UserAuthStore, PzCampaignStore, PzOAuthStore, PzStore, UiStore } from 'stores';

import { UitkPrimaryButton, UitkButtonSize } from 'uitk-react-button';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading } from 'uitk-react-text';
import { UitkCheckbox, UitkCheckboxGroup } from 'uitk-react-switch';
import { UitkLink } from 'uitk-react-link';
import { UitkLoader } from 'uitk-react-loader';

import { LocalizedTextWrapping } from 'components/localized-text-wrapping';

import { Program } from 'models';
import { Layer } from 'bernie-view';
import { UitkDialog, UitkDialogContent } from 'uitk-react-dialog';
import { RouteStore, DialogRoute } from 'bernie-core';

import updateUserProgramRelationship from 'components/signup/helper-fn/program-relationship-update-handler';

interface PzOauthFormProps {
  l10n: ILocalization;
  userAuth?: UserAuthStore;
  program: Program;
  pzCampaignStore: PzCampaignStore;
  locale: string;
  campaignTerms?: { terms: string; terms_id: string; state: string };
  pzOAuthStore?: PzOAuthStore;
  pzStore?: PzStore;
  routeStore?: RouteStore;
  uiStore?: UiStore;
}

export const PzOauthForm: React.FunctionComponent<PzOauthFormProps> = inject('uiStore')(
  ({ l10n, program, pzCampaignStore, userAuth, locale, campaignTerms, pzOAuthStore, pzStore, routeStore, uiStore }) => {
    const history = useHistory();

    const [agreed, setAgreed] = React.useState(false);
    const [joining, setJoining] = React.useState(false);
    const [pzFormLoaded, setPzFormLoaded] = React.useState(false);
    const [authToken, setAuthToken] = React.useState('');

    const programTermsAndConditionsLabel = l10n.formatText(
      'pz.signup.form.terms_and_conditions.program_terms',
      program.title,
    );

    const partnerId = null;

    const onMessageHandler = (event) => {
      let data;
      if (typeof event.data === 'string' || event.data instanceof String) {
        data = JSON.parse(event.data);
        setAuthToken(data.code);
      }
    };

    const handleIframeLoad = () => {
      setPzFormLoaded(true);
    };

    window.addEventListener('message', onMessageHandler);

    const pzOauthUrl = `https://api.partnerize.com/oauth/auth?response_type=code&client_id=${uiStore.pzClientId}&redirect_uri=${uiStore.pzRedirectUri}`;

    const handleClick = () => {
      routeStore.updateSearch({ pwaDialog: 'oauth' });
    };

    const onDismiss = () => {
      routeStore.closeDialog();
    };

    const getPartnerIdAndAssignCampaign = async (token) => {
      await pzStore.fetchPartner(token).then((partner) => {
        const id = partner.publisher_id;
        pzStore.assignCampaign(program.name, id, campaignTerms.terms_id, token).then(async (data) => {
          await updateUserProgramRelationship(userAuth, program, id, data.body.camref, partner.account_name);
          redirectToConfirmation(data.body.camref, data.body.partnerId, true);
        });
      });
    };

    const redirectToConfirmation = (camref, partnerid, existingRegistration) => {
      const location = {
        pathname: `/${locale}/join-program-confirmation`,
        state: {
          camRef: `${camref}`,
          partnerId: `${partnerid}`,
          existingRegistration,
          byOAuth: true,
          program: {
            name: program.name,
            title: program.title,
            locale: program.locale,
            networkTrackingBaseUrl: program.networkTrackingBaseUrl,
            siteUrl: program.siteUrl,
            creativeRef: program.creativeRef,
          },
        },
      };
      history.push(location);
    };

    React.useEffect(() => {
      if (authToken.length) {
        pzOAuthStore.fetchAccessToken(authToken).then((accessToken) => {
          setJoining(true);
          setPzFormLoaded(false);
          getPartnerIdAndAssignCampaign(accessToken);
        });
      }
    }, [authToken]);

    return (
      <div className="PzOauthForm">
        <DialogRoute id="oauth">
          <Layer id="dialog" onDismiss={onDismiss}>
            <UitkDialog returnFocusOnClose={true}>
              <UitkDialogContent key="dialogContent1" className="PzOauthForm__dialog">
                {!pzFormLoaded && <UitkLoader className="PzOauthForm__loader" />}
                <iframe src={pzOauthUrl} onLoad={handleIframeLoad} />
              </UitkDialogContent>
            </UitkDialog>
          </Layer>
        </DialogRoute>
        <UitkSpacing margin={{ blockend: 'four' }}>
          <div>
            <UitkSpacing margin={{ blockstart: 'four', blockend: 'three' }}>
              <UitkHeading tag="h4" size={5}>
                <LocalizedText message="pz.signup.form.terms_and_conditions.title.review_our_terms_and_conditions" />
              </UitkHeading>
            </UitkSpacing>
            <div className={`PzCheckbox ${pzCampaignStore.campaignState === 'pending' ? 'PzCheckbox--pending' : ''}`}>
              <UitkLoader isSmall={true} className="PzCheckbox__spinner" />
              <UitkCheckbox
                id="pzSignupProgramTc"
                data-stid="pzSignupProgramTc"
                label={programTermsAndConditionsLabel}
                disabled={!(pzCampaignStore?.campaignTerms?.length > 0)}
                checked={agreed}
                onChange={() => {
                  setAgreed(!agreed);
                }}
              >
                <LocalizedTextWrapping
                  message="pz.signup.form.terms_and_conditions.i_have_read_and_accept_the_program_terms"
                  fieldName="termsAndConditions"
                >
                  <UitkLink inline={true}>
                    <Link
                      to={{
                        pathname: `/${locale}/entity-terms-and-conditions`,
                        search: `?programName=${program.name}`,
                      }}
                      target={'_blank'}
                    >
                      <LocalizedText
                        message="pz.signup.form.terms_and_conditions.program_terms"
                        data={[program.title]}
                      />
                    </Link>
                  </UitkLink>
                </LocalizedTextWrapping>
              </UitkCheckbox>
            </div>
          </div>
        </UitkSpacing>

        <UitkPrimaryButton disabled={!agreed} size={UitkButtonSize.LARGE} onClick={handleClick}>
          {joining ? <UitkLoader isSmall={true} isDarkBackground={true} /> : 'Join Program with Partnerize'}
        </UitkPrimaryButton>
      </div>
    );
  },
);
